import React, { useEffect, useState } from 'react';
import SearchIcon from '@atlaskit/icon/glyph/search';
import './search.less';
import analytics from '../../utils/analytics/analytics';
import { track } from '../atlas-link';

interface Props {
    onSearch: (searchValue: string) => void;
    placeholder?: string;
    initialValue?: string;
}

interface TrackEventProps {
    action: string;
    component: string;
    searchValue?: string;
}

export const handleOnSearch = (searchValue: string) => {
    const encodedUrl = `/search-results/?searchTerm=${encodeURIComponent(
        searchValue
    )}`;
    track(encodedUrl, 'Search', 'search button');
    window.open(encodedUrl, '_self');
};

const trackAnalyticsEvent = ({
    action,
    component,
    searchValue
}: TrackEventProps) => {
    analytics.trackEvent('search', {
        // @ts-ignore
        event: action,
        eventComponent: component,
        actionSubjectId: 'searchField',
        category: 'search',
        action,
        label: 'search field',
        ...(searchValue && { searchValue })
    });
};

const handleOnFocus = () => {
    trackAnalyticsEvent({
        action: 'focused',
        component: 'searchInput'
    });
};

export const Search = ({
    onSearch,
    placeholder = '',
    initialValue = ''
}: Props) => {
    const [value, setValue] = useState<string>('');
    const [initialSearchTriggered, setInitialSearchTriggered] =
        useState<boolean>(false);

    useEffect(() => {
        if (initialValue && !initialSearchTriggered) {
            setValue(initialValue);
            onSearch(initialValue);
            setInitialSearchTriggered(true);
        }
    }, [initialValue, initialSearchTriggered, onSearch]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setValue(value);
    };

    const handleKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            trackAnalyticsEvent({
                action: 'entered',
                component: 'searchInput',
                searchValue: value
            });
            onSearch(value);
        }
    };

    const handleOnClick = () => {
        trackAnalyticsEvent({
            action: 'clicked',
            component: 'searchIcon',
            searchValue: value
        });
        onSearch(value);
    };

    return (
        <div className="search">
            <input
                data-testid="search-input"
                aria-label="Search site content"
                className="search__input"
                type="search"
                name="query"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
                onFocus={handleOnFocus}
            />
            <div
                data-testid="search-icon"
                className="search__icon"
                role="button"
                aria-label="Search site content"
                tabIndex={0}
                onClick={handleOnClick}
                onKeyUp={handleKeyUp}
            >
                <SearchIcon label="Search" />
            </div>
        </div>
    );
};
